/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import {
  Button,
  Divider,
  Grid2,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ContentQueryParams, ContentResponse, SubCategoryType } from '../../../../data/types/content.interface';
import SearchBox from '../../../shared/components/SearchBox';
import SortableTableHead, { SortableTableHeadCell } from '../../../shared/components/SortableTableHead';
import ContentRowForTag from '../../../tags/contentRowForTags';
import FilterModal from '../../../tags/filterModal';
import AddContentTypeAsset from '../addContentTypeAsset';
import Chips from '../../../shared/components/Chips';
import FinalFileContentDetails from '../../../content-creation/components/partB/finalFileContentDetails';
import { isEmpty } from 'lodash';
import usePaginatedContent from '../../../shared/components/usePaginatedContent';
import { TagType } from '../../../../data/types/tags.interface';
import ThemeService from '../../../tags/theme/services/theme.services';
import ContentsService from '../../../content-creation/services/contents.services';

const useStyles = makeStyles({
  header: {
    fontSize: '36px',
  },
  table: {
    '& .MuiTableHead-root': {
      backgroundColor: '#30343F',
      color: '#fff !important',
      fontSize: '17px',
      '& .MuiCheckbox-root': {
        color: '#fff !important',
      },
      '& .MuiTableSortLabel-root.Mui-active': {
        color: '#fff !important',
        '&:hover': {
          color: '#fff !important',
        },
      },
      '& .MuiTableSortLabel-icon': {
        color: '#fff !important',
      },
    },
  },
  clearAll: {
    color: '#30343F !important',
    fontFamily: 'Lato !important',
    fontSize: '12px !important',
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    lineHeight: 'normal !important',
  },
});

const ContentTypeAssetByBook: React.FC = () => {
  const classes = useStyles();
  const [columnHeader, setColumnHeader] = useState<SortableTableHeadCell<any>[]>([]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [assetId, setAssetId] = useState<number | null>(null);
  const [refresh, setRefresh] = useState(false);
  const [themes, setThemes] = React.useState<TagType[]>([]);
  const [selectedThemes, setSelectedThemes] = React.useState<TagType[]>([]);
  const [selectedCategory, setSelectedCategory] = React.useState<SubCategoryType[]>([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState<SubCategoryType[]>([]);
  const {
    contentCollections,
    listFilterParams,
    setListFilterParams,
    totalCount,
    contentTypes,
    categories,
    subCategories,
  } = usePaginatedContent({ type: 'final', contentType: 'book' });

  const getAllThemes = async () => {
    const result = await ThemeService.get('theme');
    if (result) {
      setThemes(result);
    }
  };

  useEffect(() => {
    setColumnHeader([
      { id: 'id', label: 'No.', numeric: true, sortable: false },
      { id: 'folder', label: 'Content Folder', numeric: false, sortable: true },
      { id: 'fileType', label: 'File Type', numeric: false, sortable: false },
      { id: 'filename', label: 'File Name', numeric: false, sortable: false },
      { id: 'title', label: 'Title', numeric: false, sortable: false },
      { id: 'theme', label: 'Theme', numeric: false, sortable: false },
      { id: 'category', label: 'Category', numeric: false, sortable: false },
      { id: 'subcategory', label: 'Sub Category', numeric: false, sortable: false },
    ]);
    getAllThemes();
  }, []);

  useEffect(() => {
    setSelectedCategory(listFilterParams.category || []);
    setSelectedSubCategory(listFilterParams.subCategory || []);
    setSelectedThemes(listFilterParams.themes || []);
  }, [listFilterParams]);

  useEffect(() => {
    if (refresh) {
      setRefresh(false);
      setListFilterParams((prev) => ({ ...prev, page: prev.page + 1 }));
    }
  }, [refresh]);

  const onSearchBoxSubmit = (q: string) => {
    const newParams = { ...listFilterParams, q, page: 1 };

    if (isEmpty(q)) {
      const { q: _, ...rest } = newParams;
      setListFilterParams(rest);
    } else {
      setListFilterParams(newParams);
    }
  };

  const handleFilterOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAppliedFilters = (params: ContentQueryParams) => {
    const props = { ...listFilterParams, ...params };
    props.contentType = 'book';
    setListFilterParams(props);
    setOpenFilters(false);
  };

  const handleCloseFilters = () => {
    setOpenFilters(false);
    setListFilterParams({
      perPage: 50,
      page: 1,
      contentType: 'book',
    });
  };

  const rowClickHandler = (property: any) => {
    setOpenDetailsModal(true);
    setAssetId(property.id);
  };

  const fetchFilterCount = () => {
    return ContentsService.getAppliedFiltersCount(listFilterParams);
  };

  const updateAll = (newParams: any) => {
    if (newParams.hasOwnProperty('themes')) {
      listFilterParams.themes = newParams?.themes;
    }
    if (newParams.hasOwnProperty('subCategory')) {
      listFilterParams.subCategory = newParams?.subCategory;
    }
    if (newParams.hasOwnProperty('category')) {
      listFilterParams.category = newParams?.category;
    }

    setListFilterParams({
      ...listFilterParams,
      page: 1,
    });
  };

  const handleCancel = () => {
    setSelectedCategory([]);
    setSelectedSubCategory([]);
    setSelectedThemes([]);
    handleCloseFilters();
  };

  return (
    <Grid2 container direction="column">
      <Grid2 container justifyContent="space-between" alignItems="center" height="84px">
        <Grid2 container pl="20px">
          <span className={classes.header}>Books</span>
        </Grid2>
        <Grid2 container justifyContent="flex-end" mr="10px">
          <Grid2 container direction="column">
            <FilterModal
              handleFilters={handleAppliedFilters}
              open={openFilters}
              anchorEl={anchorEl}
              handleClose={handleCloseFilters}
              categories={categories}
              subCategories={subCategories}
              themeList={themes}
              tagType=""
            />
          </Grid2>
          <Grid2 container>
            <SearchBox
              query={listFilterParams.q}
              onSubmit={onSearchBoxSubmit}
              isFiltersPresent
              filtersCount={fetchFilterCount()}
              width={180}
              setOpenFilters={setOpenFilters}
              setFiltersAnchorEl={handleFilterOpen}
              placeholder="Search file"
            />
          </Grid2>
        </Grid2>
      </Grid2>

      {fetchFilterCount() > 0 && listFilterParams && (
        <>
          <Grid2>
            <Divider
              orientation="horizontal"
              flexItem
              sx={{
                backgroundColor: '#E1E3E8',
                paddingBottom: '1px',
              }}
            />
          </Grid2>
          <Grid2 container mb={1} height="45px" display="flex" alignItems="center" padding="10px 10px 10px 20px">
            <Chips
              selectedThemes={selectedThemes}
              setSelectedThemes={setSelectedThemes}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              selectedSubCategory={selectedSubCategory}
              setSelectedSubCategory={setSelectedSubCategory}
              updateAll={updateAll}
            />

            {fetchFilterCount() > 0 && (
              <Grid2 container>
                <Button size="small" onClick={handleCancel} className={classes.clearAll}>
                  <span style={{ fontSize: '12px', textTransform: 'none' }}>CLEAR ALL</span>
                </Button>
              </Grid2>
            )}
          </Grid2>
        </>
      )}

      <TableContainer className={classes.table} sx={{ overflow: 'hidden' }}>
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <SortableTableHead
            onRequestSort={() => null}
            order={'asc'}
            orderBy={''}
            headCells={columnHeader}
            headerVariant="subtitle2"
            checkboxSelection={false}
            tableAssetType="assetType"
          />

          {contentCollections?.length > 0 ? (
            <TableBody sx={{ color: '#303340' }}>
              {contentCollections.map((content: ContentResponse, index: number) => (
                <ContentRowForTag
                  key={`content-${content.id}`}
                  content={content}
                  showTheme
                  index={index === 0 ? 1 : index + 1}
                  onRowClicked={() => rowClickHandler(content)}
                />
              ))}
            </TableBody>
          ) : (
            <TableBody sx={{ color: '#303340' }}>
              <TableRow>
                <TableCell colSpan={8} align="center">
                  You have not yet created any content.
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
        {totalCount > 0 && (
          <Grid2 container justifyContent="center" mt={2} mb={2}>
            <TablePagination
              component="div"
              count={totalCount}
              page={listFilterParams.page - 1}
              rowsPerPage={listFilterParams.perPage}
              onPageChange={(_, newPage) =>
                setListFilterParams(prev => ({
                  ...prev,
                  page: newPage + 1
                }))
              }
              onRowsPerPageChange={(e) =>
                setListFilterParams(prev => ({
                  ...prev,
                  perPage: parseInt(e.target.value, 10),
                  page: 1
                }))
              }
              sx={{
                '& .MuiTablePagination-toolbar': {
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                },
                '& .MuiTablePagination-spacer': {
                  display: 'none',
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  marginTop: 2,
                },
              }}
            />
          </Grid2>
        )}

        {openAddModal && <AddContentTypeAsset setOpenPopup={setOpenAddModal} contentTypes={contentTypes} />}

        {openDetailsModal && assetId && (
          <FinalFileContentDetails setOpenDetailsModal={setOpenDetailsModal} id={assetId} setRefresh={setRefresh} />
        )}
      </TableContainer>
    </Grid2>
  );
};

export default ContentTypeAssetByBook;
